import { useCallback, useEffect, useState } from 'react'
import { api } from '../../api/client'
import { useStores } from '../../app/StoreContext'
import { useMe } from '../../app/useMe'
import type {
  CalendarDerogation,
  CalendarResponse,
  CalendarSlot,
  Holiday,
  LeadTimeRule,
  MinorSupplier,
  RejectionReason,
  SpecialStore,
  Supplier,
  SupplierLimits,
} from '../../api/types'
import { Button, Card, Empty, ErrorBox, Field, Num, Spinner, Badge, formatDate } from '../../ui/primitives'

type Tab = 'calendar' | 'holidays' | 'exceptions' | 'limits' | 'leadTimes' | 'reasons'

const TABS: { id: Tab; label: string }[] = [
  { id: 'calendar', label: 'Calendario consegne CEDI' },
  { id: 'holidays', label: 'Giorni festivi' },
  { id: 'exceptions', label: 'Eccezioni al riordino' },
  { id: 'limits', label: 'Limiti fornitore' },
  { id: 'leadTimes', label: 'Tempi medi di consegna' },
  { id: 'reasons', label: 'Motivazioni di rifiuto' },
]

export function ConfigPage() {
  const [tab, setTab] = useState<Tab>('calendar')

  return (
    <>
      <div className="page-head">
        <h1>Configurazioni</h1>
      </div>

      <nav className="inline" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <Button key={t.id} variant={tab === t.id ? 'primary' : 'default'} onClick={() => setTab(t.id)}>
            {t.label}
          </Button>
        ))}
      </nav>

      {tab === 'calendar' && <CalendarSection />}
      {tab === 'holidays' && <HolidaysSection />}
      {tab === 'exceptions' && <ExceptionsSection />}
      {tab === 'limits' && <LimitsSection />}
      {tab === 'leadTimes' && <LeadTimesSection />}
      {tab === 'reasons' && <ReasonsSection />}
    </>
  )
}

/**
 * Calendario consegne CEDI.
 *
 * È l'unica configurazione in sola lettura, e per un motivo preciso: lo stesso calendario lo
 * legge il motore di previsione per decidere quando le proposte devono essere pronte. Se KPO
 * ne tenesse una copia modificabile, le due letture divergerebbero. Lo diciamo a video invece
 * di mostrare campi disabilitati senza spiegazione.
 */
function CalendarSection() {
  const { selected, storeFilter, storesReady, storeName } = useStores()

  const [standard, setStandard] = useState<CalendarResponse<CalendarSlot>>()
  const [derogations, setDerogations] = useState<CalendarResponse<CalendarDerogation>>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const [from] = useState(() => new Date().toISOString().slice(0, 10))
  const [to] = useState(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 3)
    return d.toISOString().slice(0, 10)
  })

  const load = useCallback(() => {
    // Senza l'elenco dei punti vendita non si sa se «tutti spuntati» siano tutti, e il filtro
    // partirebbe per esteso: si aspetta. Nessuna selezione, invece, non è un vicolo cieco —
    // vale «tutti i punti vendita visibili», come nelle altre schermate.
    if (!storesReady) return

    setLoading(true)
    setError(undefined)

    Promise.all([
      api.get<CalendarResponse<CalendarSlot>>('/config/cedi-calendar/standard', { storeCode: storeFilter }),
      api.get<CalendarResponse<CalendarDerogation>>('/config/cedi-calendar/derogations', {
        storeCode: storeFilter,
        from,
        to,
      }),
    ])
      .then(([s, d]) => {
        setStandard(s)
        setDerogations(d)
      })
      .catch(setError)
      .finally(() => setLoading(false))
  }, [storeFilter, storesReady, from, to])

  useEffect(load, [load])

  if (loading || !storesReady) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const dayLabels = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato']

  // Con una selezione esplicita le righe sono quelle: un punto vendita scelto che non riceve
  // niente dal centro va visto, con la riga vuota, altrimenti sembrerebbe dimenticato. Senza
  // selezione le righe sono quelle che il server ha restituito — tutti i visibili.
  const rows =
    selected.length > 0
      ? selected
      : [...new Set(standard?.entries.map((e) => e.store) ?? [])].sort()

  return (
    <>
      {standard && !standard.editable && (
        <div className="notice notice-info">
          <strong>Sola lettura</strong>
          <p>{standard.source}</p>
        </div>
      )}

      <Card
        title={
          selected.length > 0 ?
            `Settimana tipo · ${selected.length} punti vendita selezionati`
          : `Settimana tipo · tutti i punti vendita visibili (${rows.length})`
        }
      >
        {rows.length === 0 ?
          <Empty>Nessun punto vendita riceve merce dal centro di distribuzione.</Empty>
        : <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Punto vendita</th>
                {dayLabels.map((d) => (
                  <th key={d}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((code) => (
                <tr key={code}>
                  <td>
                    <span className="muted">{code}</span> {storeName(code)}
                  </td>
                  {days.map((day) => {
                    const slot = standard?.entries.find((e) => e.store === code && e.day === day)
                    return (
                      <td key={day}>
                        {slot ? (
                          <Badge tone={slot.slot === 'Morning' ? 'info' : 'warn'}>{slotLabel(slot.slot)}</Badge>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        }
        <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
          Una casella vuota significa che in quel giorno il punto vendita non riceve merce dal centro
          di distribuzione.
        </p>
      </Card>

      <Card title={`Deroghe dal ${formatDate(from)} al ${formatDate(to)}`}>
        {derogations && derogations.entries.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Punto vendita</th>
                  <th>Data</th>
                  <th>Fascia</th>
                </tr>
              </thead>
              <tbody>
                {derogations.entries.map((d, i) => (
                  <tr key={`${d.store}-${d.date}-${i}`}>
                    <td>
                      <span className="muted">{d.store}</span> {storeName(d.store)}
                    </td>
                    <td>{formatDate(d.date)}</td>
                    <td>
                      <Badge tone={d.slot === 'Morning' ? 'info' : 'warn'}>{slotLabel(d.slot)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            Nessuna deroga nei prossimi tre mesi: vale la settimana tipo.
          </Empty>
        )}
      </Card>
    </>
  )
}

function slotLabel(slot: string) {
  switch (slot) {
    case 'Morning':
    case 'morning':
      return 'Entro le 14'
    case 'Afternoon':
    case 'afternoon':
      return 'Dalle 14'
    default:
      return 'Nessuna'
  }
}

function HolidaysSection() {
  const [items, setItems] = useState<Holiday[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [year, setYear] = useState(new Date().getFullYear())

  const [date, setDate] = useState('')
  const [description, setDescription] = useState('')
  const [saveError, setSaveError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<Holiday[]>('/config/holidays', { year })
      .then(setItems)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [year])

  useEffect(load, [load])

  const add = async () => {
    setSaveError(undefined)
    try {
      await api.put(`/config/holidays/${date}`, { description })
      setDate('')
      setDescription('')
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  const remove = async (d: string) => {
    setSaveError(undefined)
    try {
      await api.del(`/config/holidays/${d}`)
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  return (
    <Card
      title="Giorni festivi nazionali"
      actions={
        <div className="inline">
          <label className="muted">Anno</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 100 }}
          />
        </div>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        I giorni festivi spostano in avanti le date di consegna calcolate.
      </p>

      <div className="inline" style={{ alignItems: 'flex-end', marginBottom: 16 }}>
        <Field label="Data">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Descrizione">
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="es. Ferragosto" />
        </Field>
        <Button variant="primary" onClick={add} disabled={!date || description.trim().length < 2}>
          Aggiungi
        </Button>
      </div>

      {saveError ? <ErrorBox error={saveError} /> : null}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} onRetry={load} />
      ) : items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrizione</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.date}>
                  <td>{formatDate(h.date)}</td>
                  <td>{h.description}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button variant="ghost" onClick={() => remove(h.date)}>
                      Elimina
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>Nessun giorno festivo registrato per il {year}.</Empty>
      )}
    </Card>
  )
}

/**
 * Eccezioni al motore di riordino: i fornitori e i punti vendita speciali.
 *
 * È la scheda che governa una regola che si vede altrove, e conviene dirla qui per intero perché
 * altrove si manifesta come un campo che non si può scrivere: **la proposta d'ordine non è
 * modificabile.** La quantità richiesta la calcola la previsione, e il negozio interviene solo
 * sulla quantità aggiunta. Le due liste di questa pagina sono l'unica eccezione — chi c'è dentro
 * può correggere la quantità richiesta e ordinare anche quando una previsione non esiste.
 *
 * Il divieto non è qui: è nel servizio (`ProposalService.UpdateLineAsync`), che rifiuta la
 * modifica se né il punto vendita né il fornitore sono in elenco. Questa pagina decide chi è in
 * elenco, e il campo a video si abilita di conseguenza.
 */
function ExceptionsSection() {
  return (
    <>
      <MinorSuppliersCard />
      <div style={{ height: 16 }} />
      <SpecialStoresCard />
    </>
  )
}

/** Una riga delle due liste di eccezioni, nella forma che la griglia sa mostrare. */
interface RigaEccezione {
  code: string
  name: string
  /** «Escluso da previsione»: è la spunta che rende modificabile la proposta. */
  senzaPrevisione: boolean
  /** La seconda spunta: omaggi per i fornitori, parafarmacia per i punti vendita. */
  secondaSpunta: boolean
}

type Colonna = 'code' | 'name' | 'senzaPrevisione' | 'secondaSpunta'
type Verso = 'asc' | 'desc'
type Terzo = 'tutti' | 'si' | 'no'

/**
 * Griglia delle eccezioni: ordinabile e filtrabile dalle colonne.
 *
 * Le liste sono di centotrenta righe, e un elenco così non si guarda scorrendolo: si cerca. Prima
 * il codice da aggiungere veniva nascosto dalla tendina se era già in elenco, che è il modo
 * sbagliato di rispondere a «dov'è finito il 15?» — sembrava sparito invece che già presente.
 * Adesso la tendina mostra tutto e la risposta si trova qui, filtrando la colonna.
 */
function GrigliaEccezioni({
  righe,
  etichettaEntita,
  etichettaSeconda,
  onSpunta,
  onElimina,
  vuota,
}: {
  righe: RigaEccezione[]
  etichettaEntita: string
  etichettaSeconda: string
  onSpunta: (code: string, senzaPrevisione: boolean, secondaSpunta: boolean) => void
  onElimina: (code: string) => void
  vuota: string
}) {
  const [ordine, setOrdine] = useState<{ col: Colonna; verso: Verso }>({ col: 'code', verso: 'asc' })
  const [fCode, setFCode] = useState('')
  const [fName, setFName] = useState('')
  const [fPrev, setFPrev] = useState<Terzo>('tutti')
  const [fSec, setFSec] = useState<Terzo>('tutti')

  const cambiaOrdine = (col: Colonna) =>
    setOrdine((o) => ({ col, verso: o.col === col && o.verso === 'asc' ? 'desc' : 'asc' }))

  const freccia = (col: Colonna) => (ordine.col !== col ? '' : ordine.verso === 'asc' ? ' ▲' : ' ▼')
  const passa = (valore: boolean, filtro: Terzo) =>
    filtro === 'tutti' || (filtro === 'si' ? valore : !valore)

  const viste = righe
    .filter(
      (r) =>
        r.code.toLowerCase().includes(fCode.trim().toLowerCase()) &&
        r.name.toLowerCase().includes(fName.trim().toLowerCase()) &&
        passa(r.senzaPrevisione, fPrev) &&
        passa(r.secondaSpunta, fSec),
    )
    .sort((a, b) => {
      const v = ordine.verso === 'asc' ? 1 : -1
      const x = a[ordine.col]
      const y = b[ordine.col]
      if (typeof x === 'boolean' && typeof y === 'boolean') return (Number(x) - Number(y)) * v
      return String(x).localeCompare(String(y)) * v
    })

  if (righe.length === 0) return <Empty>{vuota}</Empty>

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('code')}>
              {etichettaEntita}
              {freccia('code')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('name')}>
              Descrizione{freccia('name')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('senzaPrevisione')}>
              Escluso da previsione{freccia('senzaPrevisione')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('secondaSpunta')}>
              {etichettaSeconda}
              {freccia('secondaSpunta')}
            </th>
            <th />
          </tr>
          <tr>
            <th>
              <input
                value={fCode}
                onChange={(e) => setFCode(e.target.value)}
                placeholder="filtra…"
                style={{ width: '100%' }}
              />
            </th>
            <th>
              <input
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                placeholder="filtra…"
                style={{ width: '100%' }}
              />
            </th>
            <th>
              <select value={fPrev} onChange={(e) => setFPrev(e.target.value as Terzo)}>
                <option value="tutti">tutti</option>
                <option value="si">sì</option>
                <option value="no">no</option>
              </select>
            </th>
            <th>
              <select value={fSec} onChange={(e) => setFSec(e.target.value as Terzo)}>
                <option value="tutti">tutti</option>
                <option value="si">sì</option>
                <option value="no">no</option>
              </select>
            </th>
            <th style={{ textAlign: 'right' }} className="muted">
              {viste.length} di {righe.length}
            </th>
          </tr>
        </thead>
        <tbody>
          {viste.map((r) => (
            <tr key={r.code}>
              <td>{r.code}</td>
              <td>{r.name || <span className="muted">non in anagrafica</span>}</td>
              <td>
                <input
                  type="checkbox"
                  checked={r.senzaPrevisione}
                  onChange={(e) => onSpunta(r.code, e.target.checked, r.secondaSpunta)}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={r.secondaSpunta}
                  onChange={(e) => onSpunta(r.code, r.senzaPrevisione, e.target.checked)}
                />
              </td>
              <td style={{ textAlign: 'right' }}>
                <Button variant="ghost" onClick={() => onElimina(r.code)}>
                  Elimina
                </Button>
              </td>
            </tr>
          ))}
          {viste.length === 0 ? (
            <tr>
              <td colSpan={5} className="muted">
                Nessuna riga con questi filtri.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

function MinorSuppliersCard() {
  const [items, setItems] = useState<MinorSupplier[]>()
  const [names, setNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [saveError, setSaveError] = useState<unknown>()

  const [search, setSearch] = useState('')
  const [matches, setMatches] = useState<Supplier[]>([])
  const [chosen, setChosen] = useState('')
  const [freeGoods, setFreeGoods] = useState(false)
  const [noForecast, setNoForecast] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<MinorSupplier[]>('/config/minor-suppliers')
      .then(async (rows) => {
        setItems(rows)
        // Le ragioni sociali una per una: l'elenco è di pochi fornitori, e chiedere l'anagrafica
        // intera per decodificare due codici costerebbe più della decodifica.
        const found = await Promise.all(
          rows.map((r) =>
            api
              .get<Supplier>(`/suppliers/${encodeURIComponent(r.supplier)}`)
              .then((s) => [r.supplier, s.name] as const)
              .catch(() => [r.supplier, ''] as const),
          ),
        )
        setNames(Object.fromEntries(found))
      })
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    if (search.trim().length < 2) {
      setMatches([])
      return
    }
    let alive = true
    api
      .get<Supplier[]>('/suppliers', { search: search.trim(), take: 20 })
      .then((r) => alive && setMatches(r))
      .catch(() => alive && setMatches([]))
    return () => {
      alive = false
    }
  }, [search])

  const save = async (code: string, handlesFreeGoods: boolean, excludedFromForecast: boolean) => {
    setSaveError(undefined)
    try {
      await api.put(`/config/minor-suppliers/${encodeURIComponent(code)}`, {
        handlesFreeGoods,
        excludedFromForecast,
      })
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  const add = async () => {
    await save(chosen, freeGoods, noForecast)
    setChosen('')
    setSearch('')
    setMatches([])
    setFreeGoods(false)
    setNoForecast(true)
  }

  const remove = async (code: string) => {
    setSaveError(undefined)
    try {
      await api.del(`/config/minor-suppliers/${encodeURIComponent(code)}`)
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  return (
    <Card title="Fornitori speciali">
      <p className="muted" style={{ marginTop: 0 }}>
        Per i fornitori in questo elenco il punto vendita può <strong>modificare la quantità
        richiesta</strong> e ordinare anche senza previsione di fabbisogno. Per tutti gli altri la
        quantità la decide il motore e resta immodificabile.
      </p>

      <div className="inline" style={{ alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <Field label="Cerca fornitore">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="codice o ragione sociale"
          />
        </Field>
        <Field label="Fornitore">
          <select value={chosen} onChange={(e) => setChosen(e.target.value)} style={{ minWidth: 280 }}>
            <option value="">—</option>
            {matches.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </Field>
        <label className="inline" style={{ gap: 6 }}>
          <input type="checkbox" checked={noForecast} onChange={(e) => setNoForecast(e.target.checked)} />
          Escluso da previsione
        </label>
        <label className="inline" style={{ gap: 6 }}>
          <input type="checkbox" checked={freeGoods} onChange={(e) => setFreeGoods(e.target.checked)} />
          Ammette omaggi
        </label>
        <Button variant="primary" onClick={add} disabled={!chosen}>
          Aggiungi
        </Button>
      </div>

      {saveError ? <ErrorBox error={saveError} /> : null}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} onRetry={load} />
      ) : (
        <GrigliaEccezioni
          righe={(items ?? []).map((s) => ({
            code: s.supplier,
            name: names[s.supplier] ?? '',
            senzaPrevisione: s.excludedFromForecast,
            secondaSpunta: s.handlesFreeGoods,
          }))}
          etichettaEntita="Fornitore"
          etichettaSeconda="Omaggi"
          onSpunta={(code, senzaPrevisione, secondaSpunta) => save(code, secondaSpunta, senzaPrevisione)}
          onElimina={remove}
          vuota="Nessun fornitore speciale: la proposta è immodificabile per tutti."
        />
      )}
    </Card>
  )
}

function SpecialStoresCard() {
  const { stores } = useStores()
  const [items, setItems] = useState<SpecialStore[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [saveError, setSaveError] = useState<unknown>()

  const [chosen, setChosen] = useState('')
  const [pharmacy, setPharmacy] = useState(false)
  const [noForecast, setNoForecast] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<SpecialStore[]>('/config/special-stores')
      .then(setItems)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  const nameOf = (code: string) => stores.find((s) => s.code === code)?.name ?? ''

  const save = async (code: string, isPharmacy: boolean, excludedFromForecast: boolean) => {
    setSaveError(undefined)
    try {
      await api.put(`/config/special-stores/${encodeURIComponent(code)}`, {
        isPharmacy,
        excludedFromForecast,
      })
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  const add = async () => {
    await save(chosen, pharmacy, noForecast)
    setChosen('')
    setPharmacy(false)
    setNoForecast(true)
  }

  const remove = async (code: string) => {
    setSaveError(undefined)
    try {
      await api.del(`/config/special-stores/${encodeURIComponent(code)}`)
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  // Tutti i punti vendita, anche quelli già in elenco: se lo scegli e c'è già, la PUT ne aggiorna
  // le spunte. Nasconderli faceva sembrare sparito un codice che invece era presente.
  const candidati = [...stores].filter((s) => !s.isCedi).sort((a, b) => a.code.localeCompare(b.code))

  return (
    <Card title="Punti vendita speciali">
      <p className="muted" style={{ marginTop: 0 }}>
        I punti vendita in elenco possono <strong>variare la proposta d'ordine</strong> e ordinare
        in base all'assortimento anche senza previsione. I prodotti farmaceutici restano vietati a
        tutti, tranne a chi ha il farmacista: è la spunta «parafarmacia».
      </p>

      <div className="inline" style={{ alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <Field label="Punto vendita">
          <select value={chosen} onChange={(e) => setChosen(e.target.value)} style={{ minWidth: 280 }}>
            <option value="">—</option>
            {candidati.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </Field>
        <label className="inline" style={{ gap: 6 }}>
          <input type="checkbox" checked={noForecast} onChange={(e) => setNoForecast(e.target.checked)} />
          Escluso da previsione
        </label>
        <label className="inline" style={{ gap: 6 }}>
          <input type="checkbox" checked={pharmacy} onChange={(e) => setPharmacy(e.target.checked)} />
          Parafarmacia
        </label>
        <Button variant="primary" onClick={add} disabled={!chosen}>
          Aggiungi
        </Button>
      </div>

      {saveError ? <ErrorBox error={saveError} /> : null}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} onRetry={load} />
      ) : (
        <GrigliaEccezioni
          righe={(items ?? []).map((s) => ({
            code: s.store,
            name: nameOf(s.store),
            senzaPrevisione: s.excludedFromForecast,
            secondaSpunta: s.isPharmacy,
          }))}
          etichettaEntita="Punto vendita"
          etichettaSeconda="Parafarmacia"
          onSpunta={(code, senzaPrevisione, secondaSpunta) => save(code, secondaSpunta, senzaPrevisione)}
          onElimina={remove}
          vuota="Nessun punto vendita speciale."
        />
      )}
    </Card>
  )
}

type ColLimite = 'supplier' | 'name' | 'maxManualLines' | 'maxSpecialLines' | 'maxValue'
type Impostato = 'tutti' | 'impostati' | 'liberi'

/** Un solo campo alla volta: assente = non toccato, `null` = limite rimosso. */
interface ModificaLimite {
  maxManualLines?: number | null
  maxSpecialLines?: number | null
  maxValue?: number | null
}

/**
 * Limiti per fornitore: righe aggiunte a mano, righe da ordine speciale, tetto di valore.
 *
 * Tre cose che prima mancavano e che rendevano la pagina poco utile: la ragione sociale (un elenco
 * di codici a sei cifre non si legge), la modifica (si vedeva un vincolo e per cambiarlo si doveva
 * andare nel gestionale) e la ricerca. Gli importi ora si mostrano: nasconderli non proteggeva
 * niente — è una soglia di controllo, non un prezzo — e senza vederli il tetto non si poteva
 * correggere.
 *
 * Un campo vuoto vuol dire «nessun limite», ed è diverso da zero: zero vieta, vuoto lascia fare.
 */
function LimitsSection() {
  const { hasPermission } = useMe()
  const puoScrivere = hasPermission('config.write')

  const [items, setItems] = useState<SupplierLimits[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [saveError, setSaveError] = useState<unknown>()

  const [ordine, setOrdine] = useState<{ col: ColLimite; verso: Verso }>({ col: 'supplier', verso: 'asc' })
  const [fCode, setFCode] = useState('')
  const [fName, setFName] = useState('')
  const [fManuali, setFManuali] = useState<Impostato>('tutti')
  const [fSpeciali, setFSpeciali] = useState<Impostato>('tutti')
  const [fTetto, setFTetto] = useState<Impostato>('tutti')

  const [nuovo, setNuovo] = useState('')
  const [cerca, setCerca] = useState('')
  const [trovati, setTrovati] = useState<Supplier[]>([])

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<SupplierLimits[]>('/config/supplier-limits')
      .then(setItems)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    if (cerca.trim().length < 2) {
      setTrovati([])
      return
    }
    let vivo = true
    api
      .get<Supplier[]>('/suppliers', { search: cerca.trim(), take: 20 })
      .then((r) => vivo && setTrovati(r))
      .catch(() => vivo && setTrovati([]))
    return () => {
      vivo = false
    }
  }, [cerca])

  /**
   * La PUT è un upsert dell'intera riga: quello che non si manda si azzera. Quindi si parte dai
   * valori attuali e si sostituisce solo il campo toccato. `undefined` significa «non l'ho
   * toccato», `null` significa «togli il limite»: due cose diverse, e confonderle cancellerebbe
   * un limite ogni volta che se ne cambia un altro.
   */
  const salva = async (code: string, modifica: ModificaLimite) => {
    setSaveError(undefined)
    const attuale = items?.find((i) => i.supplier === code)
    const scegli = (nuovo: number | null | undefined, vecchio: number | null | undefined) =>
      nuovo === undefined ? (vecchio ?? null) : nuovo

    try {
      await api.put(`/config/supplier-limits/${encodeURIComponent(code)}`, {
        maxManualLines: scegli(modifica.maxManualLines, attuale?.maxManualLines),
        maxSpecialLines: scegli(modifica.maxSpecialLines, attuale?.maxSpecialLines),
        maxValue: scegli(modifica.maxValue, attuale?.maxValue),
      })
      load()
    } catch (e) {
      setSaveError(e)
    }
  }

  const cambiaOrdine = (col: ColLimite) =>
    setOrdine((o) => ({ col, verso: o.col === col && o.verso === 'asc' ? 'desc' : 'asc' }))
  const freccia = (col: ColLimite) => (ordine.col !== col ? '' : ordine.verso === 'asc' ? ' ▲' : ' ▼')
  const passa = (valore: number | undefined | null, filtro: Impostato) =>
    filtro === 'tutti' || (filtro === 'impostati' ? valore != null : valore == null)

  const righe = (items ?? [])
    .map((l) => ({ ...l, name: l.supplierName ?? '' }))
    .filter(
      (l) =>
        l.supplier.toLowerCase().includes(fCode.trim().toLowerCase()) &&
        l.name.toLowerCase().includes(fName.trim().toLowerCase()) &&
        passa(l.maxManualLines, fManuali) &&
        passa(l.maxSpecialLines, fSpeciali) &&
        passa(l.maxValue, fTetto),
    )
    .sort((a, b) => {
      const v = ordine.verso === 'asc' ? 1 : -1
      if (ordine.col === 'supplier' || ordine.col === 'name')
        return String(a[ordine.col]).localeCompare(String(b[ordine.col])) * v
      // I limiti non impostati vanno in fondo in entrambi i versi: «nessun vincolo» non è un valore
      // grande né piccolo, e mescolarlo ai numeri renderebbe l'ordinamento illeggibile.
      const x = a[ordine.col]
      const y = b[ordine.col]
      if (x == null && y == null) return 0
      if (x == null) return 1
      if (y == null) return -1
      return (x - y) * v
    })

  const aggiungi = async () => {
    await salva(nuovo, {})
    setNuovo('')
    setCerca('')
    setTrovati([])
  }

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <Card title="Limiti per fornitore">
      <p className="muted" style={{ marginTop: 0 }}>
        Quante righe aggiunte a mano e quante righe da ordine speciale sono ammesse in una proposta,
        e il tetto di valore sugli ordini speciali. <strong>Campo vuoto = nessun limite</strong>, che
        non è la stessa cosa di zero: zero vieta.
        {puoScrivere ? '' : ' Serve il permesso di scrittura sulle configurazioni per modificarli.'}
      </p>

      {puoScrivere ? (
        <div className="inline" style={{ alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
          <Field label="Cerca fornitore">
            <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="codice o ragione sociale" />
          </Field>
          <Field label="Aggiungi all'elenco">
            <select value={nuovo} onChange={(e) => setNuovo(e.target.value)} style={{ minWidth: 280 }}>
              <option value="">—</option>
              {trovati.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Button variant="primary" onClick={aggiungi} disabled={!nuovo}>
            Aggiungi
          </Button>
        </div>
      ) : null}

      {saveError ? <ErrorBox error={saveError} /> : null}

      {items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('supplier')}>
                  Fornitore{freccia('supplier')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('name')}>
                  Ragione sociale{freccia('name')}
                </th>
                <th className="num" style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('maxManualLines')}>
                  Righe aggiunte{freccia('maxManualLines')}
                </th>
                <th className="num" style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('maxSpecialLines')}>
                  Righe ordine speciale{freccia('maxSpecialLines')}
                </th>
                <th className="num" style={{ cursor: 'pointer' }} onClick={() => cambiaOrdine('maxValue')}>
                  Tetto di valore{freccia('maxValue')}
                </th>
              </tr>
              <tr>
                <th>
                  <input value={fCode} onChange={(e) => setFCode(e.target.value)} placeholder="filtra…" style={{ width: '100%' }} />
                </th>
                <th>
                  <input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="filtra…" style={{ width: '100%' }} />
                </th>
                <th>
                  <select value={fManuali} onChange={(e) => setFManuali(e.target.value as Impostato)}>
                    <option value="tutti">tutti</option>
                    <option value="impostati">con limite</option>
                    <option value="liberi">senza limite</option>
                  </select>
                </th>
                <th>
                  <select value={fSpeciali} onChange={(e) => setFSpeciali(e.target.value as Impostato)}>
                    <option value="tutti">tutti</option>
                    <option value="impostati">con limite</option>
                    <option value="liberi">senza limite</option>
                  </select>
                </th>
                <th>
                  <select value={fTetto} onChange={(e) => setFTetto(e.target.value as Impostato)}>
                    <option value="tutti">tutti</option>
                    <option value="impostati">con tetto</option>
                    <option value="liberi">senza tetto</option>
                  </select>
                  <span className="muted"> {righe.length} di {items.length}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {righe.map((l) => (
                <tr key={l.supplier}>
                  <td>{l.supplier}</td>
                  <td>{l.name || <span className="muted">non in anagrafica</span>}</td>
                  <td className="num">
                    <CellaLimite
                      valore={l.maxManualLines}
                      modificabile={puoScrivere}
                      onSalva={(v) => salva(l.supplier, { maxManualLines: v })}
                    />
                  </td>
                  <td className="num">
                    <CellaLimite
                      valore={l.maxSpecialLines}
                      modificabile={puoScrivere}
                      onSalva={(v) => salva(l.supplier, { maxSpecialLines: v })}
                    />
                  </td>
                  <td className="num">
                    <CellaLimite
                      valore={l.maxValue}
                      modificabile={puoScrivere}
                      decimali
                      onSalva={(v) => salva(l.supplier, { maxValue: v })}
                    />
                  </td>
                </tr>
              ))}
              {righe.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    Nessun fornitore con questi filtri.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>Nessun limite impostato: tutti i fornitori sono senza vincoli.</Empty>
      )}
    </Card>
  )
}

/**
 * Una cella di limite: numero o vuoto.
 *
 * Salva quando il campo perde il fuoco e solo se il valore è cambiato, così una tabella di
 * settecento righe non diventa settecento richieste. Il vuoto è un valore legittimo — «nessun
 * limite» — e va distinto da zero, che invece vieta: per questo si spedisce <c>null</c> e non 0.
 */
function CellaLimite({
  valore,
  modificabile,
  decimali,
  onSalva,
}: {
  valore?: number | null
  modificabile: boolean
  decimali?: boolean
  onSalva: (v: number | null) => void
}) {
  const [testo, setTesto] = useState(valore == null ? '' : String(valore))

  useEffect(() => setTesto(valore == null ? '' : String(valore)), [valore])

  if (!modificabile)
    return valore == null ? <span className="muted">nessun limite</span> : <Num value={valore} />

  const conferma = () => {
    const pulito = testo.trim().replace(',', '.')
    const v = pulito === '' ? null : Number(pulito)
    if (v !== null && (Number.isNaN(v) || v < 0)) {
      setTesto(valore == null ? '' : String(valore))
      return
    }
    if (v === (valore ?? null)) return
    onSalva(v)
  }

  return (
    <input
      value={testo}
      onChange={(e) => setTesto(e.target.value)}
      onBlur={conferma}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      placeholder="nessuno"
      inputMode={decimali ? 'decimal' : 'numeric'}
      style={{ width: decimali ? 110 : 80, textAlign: 'right' }}
    />
  )
}

function LeadTimesSection() {
  const [items, setItems] = useState<LeadTimeRule[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api.get<LeadTimeRule[]>('/config/lead-times').then(setItems).catch(setError).finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <Card title="Tempi medi di consegna">
      <p className="muted" style={{ marginTop: 0 }}>
        Giorni lavorativi fra ordine e consegna. Le regole si applicano dalla più specifica alla più
        generica: fornitore con punto vendita, poi fornitore, poi provincia, poi regione.
      </p>

      {items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fornitore</th>
                <th>Punto vendita</th>
                <th>Provincia</th>
                <th>Regione</th>
                <th className="num">Giorni</th>
                <th>Origine</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.supplier ?? <span className="muted">tutti</span>}</td>
                  <td>{r.store ?? <span className="muted">tutti</span>}</td>
                  <td>{r.provinceCode ?? <span className="muted">—</span>}</td>
                  <td>{r.regionCode ?? <span className="muted">—</span>}</td>
                  <td className="num">
                    <Num value={r.workingDays} />
                  </td>
                  <td>
                    <Badge tone="muted">{r.source === 'import' ? 'importata' : 'manuale'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>
          Nessun tempo medio configurato: le date di consegna usano il valore predefinito del fornitore.
        </Empty>
      )}
    </Card>
  )
}

function ReasonsSection() {
  const [items, setItems] = useState<RejectionReason[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<RejectionReason[]>('/config/rejection-reasons', { activeOnly: false })
      .then(setItems)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <Card title="Motivazioni di rifiuto del documento di carico">
      {items && items.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Codice</th>
                <th>Descrizione</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.code}</td>
                  <td>{r.description}</td>
                  <td>
                    <Badge tone={r.isActive ? 'ok' : 'muted'}>{r.isActive ? 'Attiva' : 'Non attiva'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>
          Nessuna motivazione configurata. Senza motivazioni il back-office non può respingere un
          documento di carico: vanno inserite prima di usare il controllo documenti.
        </Empty>
      )}
    </Card>
  )
}
